import { useCallback, useEffect, useRef, useState } from "react";
import { PAGE_SIZE, PickerProduct, PickerQuery, ProductPageError, ProductPageLoader, uniqueProducts } from "./productSelection";

export function useProductPages(query: PickerQuery, loadPage: ProductPageLoader, limit = PAGE_SIZE) {
  const [products, setProducts] = useState<PickerProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [more, setMore] = useState(false);
  const key = JSON.stringify(query);
  const currentKey = useRef(key);
  currentKey.current = key;
  const generation = useRef(0);
  const state = useRef({ page: 0, busy: false, more: true });
  const loader = useRef(loadPage);
  loader.current = loadPage;
  const currentQuery = useRef(query);
  currentQuery.current = query;
  const started = useRef(false);
  const pageSize = useRef(limit);
  pageSize.current = limit;
  const browseKey = JSON.stringify({ ...query, revision: undefined });
  const previousBrowseKey = useRef(browseKey);

  const next = useCallback(async () => {
    if (state.current.busy || !state.current.more) return;
    const requestGeneration = generation.current;
    const requestKey = currentKey.current;
    const page = state.current.page;
    state.current.busy = true;
    setLoading(true);
    setError("");
    try {
      const size = pageSize.current;
      const result = await loader.current(currentQuery.current, page, size);
      if (requestGeneration !== generation.current || requestKey !== currentKey.current) return;
      setProducts((previous) => uniqueProducts(page === 0 ? result.products : [...previous, ...result.products]));
      setTotal(result.total);
      state.current.page = page + 1;
      // Count consumed server slots, not deduplicated rows; repeated IDs must not cause endless requests.
      state.current.more = result.products.length > 0 && (page + 1) * size < result.total;
      setMore(state.current.more);
    } catch (failure) {
      if (requestGeneration !== generation.current || requestKey !== currentKey.current) return;
      setError(failure instanceof ProductPageError ? failure.message : "We couldn't load products. Check your connection and try again.");
    } finally {
      if (requestGeneration === generation.current && requestKey === currentKey.current) {
        state.current.busy = false;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    generation.current += 1;
    state.current = { page: 0, busy: false, more: true };
    // Keep confirmed prices/results on a refresh failure. A changed filter still clears old rows.
    if (previousBrowseKey.current !== browseKey) { setProducts([]); setTotal(0); }
    previousBrowseKey.current = browseKey;
    setMore(false); setError(""); setLoading(true);
    const timer = window.setTimeout(() => { void next(); }, started.current ? 300 : 0);
    started.current = true;
    return () => { window.clearTimeout(timer); generation.current += 1; };
  }, [key, browseKey, next]);
  const patchProduct = (product: PickerProduct) => setProducts((rows) => rows.map((row) => row.productUniqueId === product.productUniqueId ? product : row));
  return { products, loading, error, total, more, next, patchProduct };
}
